import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ModalComponent } from '../../modules/modal.module/modal/modal.component';
import { Observable, Subscriber } from 'rxjs';
import { ConfirmDialogResult } from '../confirm-dialog.component/confirm-dialog.component';

export interface PromptDialogField {
  key: string;
  label: string;
  type?: 'text' | 'number';
  value?: string | number;
  required?: boolean;
  error?: string;
}

export interface PromptDialogResult {
  action: ConfirmDialogResult;
  values: { [key: string]: string | number | null };
}

@Component({
  selector: 'prompt-dialog',
  templateUrl: './prompt-dialog.component.html',
  styleUrls: ['./prompt-dialog.component.css']
})
export class PromptDialogComponent {

  @Input() title: string = '';
  @Input() subject: string = '';
  @Input() fields: PromptDialogField[] = [];

  // Message d'erreur global affiché en rouge au-dessus des inputs
  globalError: string = '';

  @Output() onAccept: EventEmitter<void> = new EventEmitter();
  @Output() onReject: EventEmitter<void> = new EventEmitter();
  @Output() change: EventEmitter<{ [key: string]: string | number }> = new EventEmitter();

  @ViewChild(ModalComponent, { read: ModalComponent, static: false }) modal!: ModalComponent;

  private _subscriber!: Subscriber<PromptDialogResult>;
  // true si 2+ types → règle "au moins un"
  private _atLeastOneMode: boolean = false;

  constructor() {}

  // Vérifie si au moins un champ est rempli (mode 2-3 types)
  get hasAtLeastOneFilled(): boolean {
    if (!this._atLeastOneMode) return true;
    return this.fields.some(f => f.value && f.value.toString().trim() !== '');
  }

  // Validation champs required (mode 1 type)
  validateFields(showErrors: boolean = true): boolean {
    let isValid = true;

    this.fields.forEach(field => {
      if (field.required && (!field.value || field.value.toString().trim() === '')) {
        isValid = false;
        if (showErrors) {
          field.error = `${field.label} est obligatoire`;
        }
      } else {
        field.error = '';
      }
    });

    return isValid;
  }

  accept(): void {
    // Mode "au moins un" → vérifier qu'un champ est rempli
    if (this._atLeastOneMode) {
      if (!this.hasAtLeastOneFilled) {
        this.globalError = 'Veuillez renseigner au moins un nom de modèle !';
        return;
      }
      this.globalError = '';
    } else {
      // Mode "1 type" → validation required classique
      if (!this.validateFields(true)) {
        return;
      }
    }

    const values: { [key: string]: string | number | null } = {};
    this.fields.forEach(field => {
      values[field.key] = field.value ?? null;
    });

    this._subscriber.next({
      action: ConfirmDialogResult.ACCEPTED,
      values: values
    });

    this.onAccept.emit();
    this._subscriber.complete();
    this.modal.close();
  }

  reject(): void {
    this._subscriber.next({
      action: ConfirmDialogResult.REJECTED,
      values: {}
    });

    this.onReject.emit();
    this._subscriber.complete();
    this.modal.close();
  }

  onFieldChange(): void {
    const values: { [key: string]: string | number } = {};

    this.fields.forEach(field => {
      values[field.key] = field.value ?? '';
    });

    // Effacer l'erreur globale dès qu'un champ est rempli
    if (this._atLeastOneMode && this.hasAtLeastOneFilled) {
      this.globalError = '';
    }

    // Effacer les erreurs individuelles en live
    if (!this._atLeastOneMode) {
      this.validateFields(false);
    }

    this.change.emit(values);
  }

  open(options?: { 
    title?: string; 
    subject?: string; 
    fields?: PromptDialogField[];
    atLeastOneMode?: boolean;  // ← nouveau flag
  }): Observable<PromptDialogResult> {

    if (options?.title)   this.title   = options.title;
    if (options?.subject) this.subject = options.subject;
    if (options?.fields)  this.fields  = options.fields;

    // Activer le mode "au moins un" si 2+ champs
    this._atLeastOneMode = options?.atLeastOneMode ?? false;

    // Reset erreurs
    this.globalError = '';
    this.fields.forEach(field => {
      field.error = '';
      field.value = field.value ?? '';
    });

    return new Observable<PromptDialogResult>(subscriber => {
      this._subscriber = subscriber;
      this.modal.open();
    });
  }
}